import { Feature, FeatureToggleConfig } from '@shopee-user/feature-toggle';
import { isVersionReleased } from './utils/version';
import {
  VERSION_RELEASE_IOS_ACCOUNT_DELETION,
  VERSION_RELEASE_IOS_ANDROID_ACCOUNT_DELETION_VN,
  VERSION_RELEASE_ANDROID_ACCOUNT_DELETION,
  VERSION_RELEASE_WEB_BRIDGE_NOTIFY_LOGIN_ERROR,
  VERSION_RELEASE_CHANGE_PHONE,
  VERSION_RELEASE_CHANGE_EMAIL_DEFAULT,
  VERSION_RELEASE_CHANGE_EMAIL_VN,
  VERSION_RELEASE_IOS_ANDROID_BIOMETRICS_DEFAULT,
  VERSION_RELEASE_IOS_ANDROID_BIOMETRICS_VN,
  VERSION_RELEASE_IOS_ANDROID_WAL_ID_MY,
  VERSION_RELEASE_IOS_ANDROID_CHANGE_PASSWORD_ID_MY,
  VERSION_RELEASE_IOS_ANDROID_PH,
  VERSION_RELEASE_QUICK_LOGIN_DEFAULT,
} from './constants/versionRelease';
import { Locale } from '@shopee-user/global';

import {
  GENERAL_BASE_CONFIG,
  LOGIN_BY_PASSWORD_BASE_CONFIG,
  LOGIN_BY_OTP_BASE_CONFIG,
  LOGIN_BY_BIOMETRIC_BASE_CONFIG,
  SIGNUP_BY_OTP_BASE_CONFIG,
  RESET_PASSWORD_BASE_CONFIG,
  SHOPEE_PAY_MIGRATION_BASE_CONFIG,
  SHOPEE_PARTNER_SPECIFIC_BASE_CONFIG,
  WEBVIEW_BASE_CONFIG,
  USER_ACCOUNT_BASE_CONFIG,
  USER_PHONE_BASE_CONFIG,
  USER_EMAIL_BASE_CONFIG,
  USER_PASSWORD_BASE_CONFIG,
  OTP_SHARED_SERVICE_BASE_CONFIG,
  ONE_CREDENTIAL_BASE_CONFIG,
  ONE_CREDENTIAL_MIGRATION_BASE_CONFIG,
  ONE_CREDENTIAL_SWITCH_TOC_BASE_CONFIG,
  ONE_CREDENTIAL_DELINK_BASE_CONFIG,
  TOB_BRANDING_CONFIG,
  TEMPORARY_BASE_CONFIG,
  THIRDPARTY_INTEGRATION_BASE_CONFIG,
  IVS_BASE_CONFIG,
  NO_COST_LOGIN_METHOD_BASE_CONFIG,
} from '@shopee-user/feature-toggle-base-config';

const IvsFeatureToggleConfig = true;

export const FEATURE_TOGGLE_CONFIG: FeatureToggleConfig = {
  ...GENERAL_BASE_CONFIG,
  ...LOGIN_BY_PASSWORD_BASE_CONFIG,
  ...LOGIN_BY_OTP_BASE_CONFIG,
  ...LOGIN_BY_BIOMETRIC_BASE_CONFIG,
  ...SIGNUP_BY_OTP_BASE_CONFIG,
  ...RESET_PASSWORD_BASE_CONFIG,
  ...SHOPEE_PAY_MIGRATION_BASE_CONFIG,
  ...SHOPEE_PARTNER_SPECIFIC_BASE_CONFIG,
  ...WEBVIEW_BASE_CONFIG,
  ...USER_ACCOUNT_BASE_CONFIG,
  ...USER_PHONE_BASE_CONFIG,
  ...USER_EMAIL_BASE_CONFIG,
  ...USER_PASSWORD_BASE_CONFIG,
  ...OTP_SHARED_SERVICE_BASE_CONFIG,
  ...ONE_CREDENTIAL_BASE_CONFIG,
  ...ONE_CREDENTIAL_MIGRATION_BASE_CONFIG,
  ...ONE_CREDENTIAL_SWITCH_TOC_BASE_CONFIG,
  ...ONE_CREDENTIAL_DELINK_BASE_CONFIG,
  ...TOB_BRANDING_CONFIG,
  ...TEMPORARY_BASE_CONFIG,
  ...THIRDPARTY_INTEGRATION_BASE_CONFIG,
  ...IVS_BASE_CONFIG,
  ...NO_COST_LOGIN_METHOD_BASE_CONFIG,

  // General
  [Feature.EnableLoginTimeout]: true,
  [Feature.AppTracking]: true,
  [Feature.WebTracking]: true,
  [Feature.AccountSuspendedUseHelpCenterLink]: {
    default: {
      default: false,
      [Locale.VN]: true,
    },
  },
  [Feature.LoginHideSignupButton]: true,
  [Feature.SendLanguageInSendOtp]: true,

  // Login by Password (Email + Phone)
  [Feature.LoginByPasswordEntrance]: {
    default: {
      default: false,
      [Locale.ID]: true,
      [Locale.MY]: true,
      [Locale.PH]: true,
    },
  },
  [Feature.LoginByPasswordAllowEmail]: {
    default: {
      default: false,
      [Locale.ID]: true,
      [Locale.MY]: true,
    },
  },
  [Feature.LoginByPasswordAllowPhone]: {
    default: {
      default: false,
      [Locale.ID]: true,
      [Locale.MY]: true,
      [Locale.PH]: true,
    },
  },

  // Login by OTP
  [Feature.LoginByOtpEntrance]: true,
  [Feature.LoginByOtpAllowEmail]: {
    default: {
      default: false,
      [Locale.MY]: true,
    },
  },
  [Feature.LoginByOtpAllowPhone]: true,
  [Feature.LoginByOtpSupportWAL]: {
    default: {
      default: false,
      [Locale.MY]: isVersionReleased(VERSION_RELEASE_IOS_ANDROID_WAL_ID_MY),
      [Locale.ID]: isVersionReleased(VERSION_RELEASE_IOS_ANDROID_WAL_ID_MY),
    },
  },
  [Feature.SupportZaloOtp]: {
    default: {
      default: true,
      [Locale.PH]: false,
    },
  },
  [Feature.SupportViberOtp]: {
    default: {
      default: false,
      [Locale.PH]: true,
    },
  },

  // Signup by OTP
  [Feature.SignupByOtpEntrance]: true,
  [Feature.SignupByOtpAllowPhone]: true,
  [Feature.SignupByOtpRequirePassword]: {
    default: {
      default: false,
      [Locale.ID]: true,
      [Locale.MY]: true,
      [Locale.PH]: true,
    },
  },
  [Feature.SignupByOtpLoginUponAlreadyRegisteredWithPhone]: true,
  [Feature.SignupByOtpSupportWAL]: {
    default: {
      default: false,
      [Locale.MY]: isVersionReleased(VERSION_RELEASE_IOS_ANDROID_WAL_ID_MY),
      [Locale.ID]: isVersionReleased(VERSION_RELEASE_IOS_ANDROID_WAL_ID_MY),
    },
  },

  // Reset password
  [Feature.ResetPasswordEntrance]: {
    default: {
      default: false,
      [Locale.ID]: true,
      [Locale.MY]: true,
      [Locale.PH]: true,
    },
  },
  [Feature.ResetPasswordByPhone]: {
    default: {
      default: false,
      [Locale.ID]: true,
      [Locale.MY]: true,
      [Locale.PH]: true,
    },
  },
  [Feature.ResetPasswordByEmail]: {
    default: {
      default: false,
      [Locale.ID]: true,
      [Locale.MY]: true,
    },
  },
  [Feature.ResetPasswordCallResetPasswordInit]: {
    default: {
      default: false,
      [Locale.ID]: true,
      [Locale.MY]: true,
    },
  },

  // WebView
  [Feature.WebViewNotifyLoginError]: isVersionReleased(
    VERSION_RELEASE_WEB_BRIDGE_NOTIFY_LOGIN_ERROR
  ),
  [Feature.OpenAccountDeleteLinkAsExternalUrl]: true, // to open foody:// deeplink
  [Feature.WebViewLogoutAfterCredentialChange]: true,
  [Feature.NotifyRefreshToken]: true,

  // Account pages
  [Feature.UserAccountEntrance]: true,
  [Feature.UserManageEmail]: {
    default: {
      default: true,
      [Locale.PH]: false,
    },
  },
  [Feature.UserManagePhoneNumber]: true,
  [Feature.UserManagePassword]: {
    default: {
      default: false,
      [Locale.ID]: isVersionReleased(
        VERSION_RELEASE_IOS_ANDROID_CHANGE_PASSWORD_ID_MY
      ),
      [Locale.MY]: isVersionReleased(
        VERSION_RELEASE_IOS_ANDROID_CHANGE_PASSWORD_ID_MY
      ),
      [Locale.PH]: isVersionReleased(VERSION_RELEASE_IOS_ANDROID_PH),
    },
  },
  [Feature.UserManageToCLinkedEmail]: true,
  [Feature.AccountDeleteEntranceIOS]: {
    default: {
      default: false,
      [Locale.TH]: isVersionReleased(VERSION_RELEASE_IOS_ACCOUNT_DELETION),
      [Locale.MY]: isVersionReleased(VERSION_RELEASE_IOS_ACCOUNT_DELETION),
      [Locale.VN]: isVersionReleased(
        VERSION_RELEASE_IOS_ANDROID_ACCOUNT_DELETION_VN
      ),
      [Locale.PH]: isVersionReleased(VERSION_RELEASE_IOS_ANDROID_PH),
    },
  },
  [Feature.AccountDeleteEntranceAndroid]: {
    default: {
      default: false,
      [Locale.MY]: isVersionReleased(VERSION_RELEASE_ANDROID_ACCOUNT_DELETION),
      [Locale.TH]: isVersionReleased(VERSION_RELEASE_ANDROID_ACCOUNT_DELETION),
      [Locale.ID]: isVersionReleased(VERSION_RELEASE_ANDROID_ACCOUNT_DELETION),
      [Locale.VN]: isVersionReleased(
        VERSION_RELEASE_IOS_ANDROID_ACCOUNT_DELETION_VN
      ),
      [Locale.PH]: isVersionReleased(VERSION_RELEASE_IOS_ANDROID_PH),
    },
  },
  [Feature.UnmaskUserEmail]: {
    default: {
      default: false,
      [Locale.TH]: true,
      [Locale.MY]: true,
      [Locale.ID]: true,
    },
  },
  [Feature.UnmaskUserPhone]: {
    default: {
      default: false,
      [Locale.TH]: true,
      [Locale.MY]: true,
      [Locale.ID]: true,
    },
  },

  // User phone
  [Feature.UserPhoneEntrance]: {
    default: {
      default: false,
      [Locale.ID]: isVersionReleased(VERSION_RELEASE_CHANGE_PHONE),
      [Locale.TH]: isVersionReleased(VERSION_RELEASE_CHANGE_PHONE),
      [Locale.PH]: isVersionReleased(VERSION_RELEASE_IOS_ANDROID_PH),
    },
  },
  [Feature.UserChangePhone]: {
    default: {
      default: false,
      [Locale.ID]: isVersionReleased(VERSION_RELEASE_CHANGE_PHONE),
      [Locale.TH]: isVersionReleased(VERSION_RELEASE_CHANGE_PHONE),
      [Locale.PH]: isVersionReleased(VERSION_RELEASE_IOS_ANDROID_PH),
    },
  },
  [Feature.UserChangePhoneSupportWAL]: {
    default: {
      default: false,
      [Locale.ID]: isVersionReleased(VERSION_RELEASE_IOS_ANDROID_WAL_ID_MY),
    },
  },
  [Feature.CallChangePhoneInitApiInProfilePage]: {
    default: {
      default: false,
      [Locale.ID]: true,
      [Locale.TH]: true,
      [Locale.PH]: true,
    },
  },
  [Feature.ChangePhoneUseNativeVerification]: {
    default: {
      default: false,
      [Locale.ID]: true,
      [Locale.TH]: true,
      [Locale.PH]: true,
    },
  },
  [Feature.HideContactBusinessPartnerInDialog]: {
    default: {
      default: false,
      [Locale.PH]: true,
    },
  },

  // User email
  [Feature.UserEmailEntrance]: {
    default: {
      default: false,
      [Locale.ID]: isVersionReleased(VERSION_RELEASE_CHANGE_EMAIL_DEFAULT),
      [Locale.MY]: isVersionReleased(VERSION_RELEASE_CHANGE_EMAIL_DEFAULT),
      [Locale.TH]: isVersionReleased(VERSION_RELEASE_CHANGE_EMAIL_DEFAULT),
      [Locale.VN]: isVersionReleased(VERSION_RELEASE_CHANGE_EMAIL_VN),
    },
  },
  [Feature.UserChangeEmail]: {
    default: {
      default: false,
      [Locale.ID]: isVersionReleased(VERSION_RELEASE_CHANGE_EMAIL_DEFAULT),
      [Locale.MY]: isVersionReleased(VERSION_RELEASE_CHANGE_EMAIL_DEFAULT),
      [Locale.TH]: isVersionReleased(VERSION_RELEASE_CHANGE_EMAIL_DEFAULT),
      [Locale.VN]: isVersionReleased(VERSION_RELEASE_CHANGE_EMAIL_VN),
    },
  },
  [Feature.CallChangeEmailInitApiInProfilePage]: {
    default: {
      default: false,
      [Locale.ID]: true,
      [Locale.MY]: true,
      [Locale.TH]: true,
      [Locale.VN]: true,
    },
  },
  [Feature.ChangeEmailUseNativeVerification]: {
    default: {
      default: false,
      [Locale.ID]: true,
      [Locale.MY]: true,
      [Locale.TH]: true,
      [Locale.VN]: true,
    },
  },

  // User password
  [Feature.UserPasswordEntrance]: {
    default: {
      default: false,
      [Locale.ID]: isVersionReleased(
        VERSION_RELEASE_IOS_ANDROID_CHANGE_PASSWORD_ID_MY
      ),
      [Locale.MY]: isVersionReleased(
        VERSION_RELEASE_IOS_ANDROID_CHANGE_PASSWORD_ID_MY
      ),
      [Locale.PH]: isVersionReleased(VERSION_RELEASE_IOS_ANDROID_PH),
    },
  },
  [Feature.CallChangePasswordInitApiInProfilePage]: {
    default: {
      default: false,
      [Locale.ID]: true,
      [Locale.MY]: true,
      [Locale.PH]: true,
    },
  },
  [Feature.ChangePasswordUseNativeVerification]: {
    default: {
      default: false,
      [Locale.ID]: true,
      [Locale.MY]: true,
      [Locale.PH]: true,
    },
  },

  // OTP SS
  [Feature.VerifyOtpSharedService]: IvsFeatureToggleConfig,

  // IVS
  [Feature.IvsIntegration]: IvsFeatureToggleConfig,
  [Feature.VerifyPasswordSharedService]: IvsFeatureToggleConfig,
  // TODO: enable and check app version
  [Feature.BiometricsV1]: {
    default: {
      default: false,
      [Locale.ID]: isVersionReleased(
        VERSION_RELEASE_IOS_ANDROID_BIOMETRICS_DEFAULT
      ),
      [Locale.MY]: isVersionReleased(
        VERSION_RELEASE_IOS_ANDROID_BIOMETRICS_DEFAULT
      ),
      [Locale.TH]: isVersionReleased(
        VERSION_RELEASE_IOS_ANDROID_BIOMETRICS_DEFAULT
      ),
      [Locale.VN]: isVersionReleased(VERSION_RELEASE_IOS_ANDROID_BIOMETRICS_VN),
      [Locale.PH]: isVersionReleased(VERSION_RELEASE_IOS_ANDROID_PH),
    },
  },
  [Feature.BiometricsV2]: {
    default: {
      default: false,
      [Locale.ID]: isVersionReleased(
        VERSION_RELEASE_IOS_ANDROID_BIOMETRICS_DEFAULT
      ),
      [Locale.MY]: isVersionReleased(
        VERSION_RELEASE_IOS_ANDROID_BIOMETRICS_DEFAULT
      ),
      [Locale.TH]: isVersionReleased(
        VERSION_RELEASE_IOS_ANDROID_BIOMETRICS_DEFAULT
      ),
      [Locale.VN]: isVersionReleased(VERSION_RELEASE_IOS_ANDROID_BIOMETRICS_VN),
      [Locale.PH]: isVersionReleased(VERSION_RELEASE_IOS_ANDROID_PH),
    },
  },
  [Feature.LoginByBiometricsSupportWAL]: true,
  [Feature.LoginByBiometricsEmailOtp]: {
    default: {
      default: false,
      [Locale.MY]: true,
    },
  },
  [Feature.QuickLogin]: {
    default: {
      default: false,
      [Locale.ID]: isVersionReleased(VERSION_RELEASE_QUICK_LOGIN_DEFAULT),
      [Locale.MY]: isVersionReleased(VERSION_RELEASE_QUICK_LOGIN_DEFAULT),
    },
  },
};
